import React from 'react'
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
  Tooltip,
} from '@mui/material'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked'
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked'

// One colour per zone slot (cycles if >6 zones)
const ZONE_COLORS = ['#1565C0', '#2E7D32', '#AD1457', '#00838F', '#E65100', '#6A1B9A']

/**
 * ZoneEditor — left-panel row for a single delivery zone.
 *
 * Props:
 *   zone        { name, fee, polygon, sortOrder }
 *   index       number
 *   isSelected  bool
 *   onClick     () => void
 *   onNameChange (value: string) => void
 *   onFeeChange  (value: string) => void
 *   onDelete    () => void
 *   onEdit      () => void  — opens edit modal to redraw boundary
 */
const ZoneEditor = ({ zone, index, isSelected, onClick, onNameChange, onFeeChange, onDelete, onEdit }) => {
  const color = ZONE_COLORS[index % ZONE_COLORS.length]

  return (
    <Box
      onClick={onClick}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        p: 1.5,
        mb: 1,
        borderRadius: 1,
        cursor: 'pointer',
        border: isSelected ? `2px solid ${color}` : '2px solid transparent',
        backgroundColor: isSelected ? `${color}14` : 'transparent',
        '&:hover': { backgroundColor: `${color}0d` },
        transition: 'background-color 0.15s',
      }}
    >
      {/* Header row: colour icon + summary + edit + delete */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {isSelected
          ? <RadioButtonCheckedIcon sx={{ color, fontSize: 18, flexShrink: 0 }} />
          : <RadioButtonUncheckedIcon sx={{ color, fontSize: 18, flexShrink: 0 }} />
        }
        <Typography variant="body2" sx={{ fontWeight: 600, color, flex: 1 }}>
          {zone.name || `Zone ${index + 1}`}
          {zone.fee !== '' && zone.fee !== undefined ? `: $${parseFloat(zone.fee || 0).toFixed(2)}` : ''}
        </Typography>
        <Tooltip title="Edit zone boundary">
          <IconButton
            size="small"
            onClick={e => { e.stopPropagation(); onEdit(); }}
            sx={{ color: '#9e9e9e', '&:hover': { color: '#1565C0' } }}
          >
            <EditOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Delete zone">
          <IconButton
            size="small"
            onClick={e => { e.stopPropagation(); onDelete(); }}
            sx={{ color: '#9e9e9e', '&:hover': { color: '#d32f2f' } }}
          >
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Editable name + fee fields — shown when selected */}
      {isSelected && (
        <Box sx={{ display: 'flex', gap: 1, pl: 3.5 }} onClick={e => e.stopPropagation()}>
          <TextField
            label="Zone name"
            value={zone.name}
            onChange={e => onNameChange(e.target.value)}
            size="small"
            sx={{ flex: 2 }}
            placeholder="e.g. 5 miles"
          />
          <TextField
            label="Fee"
            value={zone.fee}
            onChange={e => onFeeChange(e.target.value)}
            size="small"
            sx={{ flex: 1 }}
            type="number"
            inputProps={{ min: 0, step: 0.01 }}
            InputProps={{
              startAdornment: <InputAdornment position="start">$</InputAdornment>,
            }}
          />
        </Box>
      )}
    </Box>
  )
}

export default ZoneEditor
export { ZONE_COLORS }
